import { type MouseEvent, useCallback, useState } from "react";
import { type BackdropProps, Menu as MUIMenu, type MenuProps as MUIMenuProps } from "@mui/material";
import { C_OUTLINE_VARIANT, C_PRIMARY } from "../icon/Colors";
import {
  HEADER_HEIGHT_DESKTOP,
  HEADER_HEIGHT_PHONE,
  HEADER_HEIGHT_PHONE_BIG,
  HEADER_HEIGHT_TABLET,
  MQ_HEADER_BREAKPOINT_LOGO,
} from "../../../utils/constants";
import { MENU_POS_BELOW } from "./MenuPositions";
import { makeStyles, withStyles } from "@mui/styles";
import useMediaQuery from "../../hooks/useMediaQuery";
import { MEDIA_QUERY_MAX_PHONE, MEDIA_QUERY_MIN_DESKTOP_S, MEDIA_QUERY_MIN_TABLET } from "../../../utils/screenSizes";
import { useSchaefflerTopBannerList } from "~/CMSComponents/components/SchaefflerTopBannerComponent";
import { useActiveCampaignsNotInSmartEdit } from "~/Header/components/TopBannersSection/utils";
import { showRegionBanner } from "~/RegionSelectionBanner/RegionSelectionBanner";

export type MenuProps = MUIMenuProps;

export const Menu = withStyles({
  paper: {
    boxShadow: "none",
    border: `1px solid ${C_OUTLINE_VARIANT}`,
    borderRadius: "0px",
    maxHeight: `calc(100% - ${HEADER_HEIGHT_PHONE})`,
    [`@media ${MQ_HEADER_BREAKPOINT_LOGO}`]: {
      maxHeight: `calc(100% - ${HEADER_HEIGHT_PHONE_BIG})`,
    },
    [`@media ${MEDIA_QUERY_MIN_TABLET}`]: {
      maxHeight: `calc(100% - ${HEADER_HEIGHT_TABLET})`,
    },
    [`@media ${MEDIA_QUERY_MIN_DESKTOP_S}`]: {
      maxHeight: `calc(100% - ${HEADER_HEIGHT_DESKTOP})`,
    },
  },
})(MUIMenu);

export const withNavigationMenuStyle = withStyles({
  paper: {
    [`@media ${MEDIA_QUERY_MIN_TABLET}`]: {
      borderRadius: "0px 0px 4px 4px",
      translate: "0 -3px",
      borderTop: `3px solid ${C_PRIMARY}`,
    },
  },
});

export const withSpaceInNavigationMenuStyle = withStyles({
  paper: {
    [`@media ${MEDIA_QUERY_MIN_TABLET}`]: {
      borderRadius: "0px 0px 4px 4px",
      translate: "0 -3px",
      borderTop: `3px solid ${C_PRIMARY}`,
    },
    [`@media ${MEDIA_QUERY_MAX_PHONE}`]: {
      left: "16px !important",
    },
  },
});

/**
This function is get the visible height of the banner (i.e. the part that’s currently in the viewport), not the full height of the element.
*/

function getVisibleHeightBySelector(selector: string): number {
  const el = document.querySelector(selector);
  if (!(el instanceof HTMLElement)) return 0;

  const rect = el.getBoundingClientRect();
  const windowHeight = window.innerHeight;

  // Top is below the viewport bottom OR bottom is above the viewport top
  if (rect.bottom <= 0 || rect.top >= windowHeight) return 0;

  const visibleTop = Math.max(rect.top, 0);
  const visibleBottom = Math.min(rect.bottom, windowHeight);

  return visibleBottom - visibleTop;
}

export const MenuBottomCornersRounded = withNavigationMenuStyle(Menu);

export const useFullwidthPopoverStyles = makeStyles({
  paper: {
    width: "100%",
    left: 0,
    right: 0,
    maxWidth: "unset",
  },
});

/**
 * Menu with some default configuration.
 *
 * It becomes a full width menu on small (Up to XS size) screens
 */
export const SimpleMenu = (props: MenuProps) => {
  const popoverClasses = useFullwidthPopoverStyles();
  const isMinTablet = useMediaQuery(MEDIA_QUERY_MIN_TABLET);

  let DEFAULT_SAFETY_MARGIN = 5;

  const smartEditBanners = useSchaefflerTopBannerList();
  const campaignsNotInSmartEdit = useActiveCampaignsNotInSmartEdit();
  if (smartEditBanners.length || campaignsNotInSmartEdit.length) {
    DEFAULT_SAFETY_MARGIN += getVisibleHeightBySelector(".scha__root-sales-banner");
  }

  if (showRegionBanner()) {
    DEFAULT_SAFETY_MARGIN += getVisibleHeightBySelector(".scha__root-region-banner");
  }

  const dynamicBackdropProps = {
    ...FULLWIDTH_MENU_BACKDROP_PROPS,
    style: {
      "--safety-margin": `${DEFAULT_SAFETY_MARGIN}px`,
    } as React.CSSProperties,
  };

  const viewportSpecificProps = isMinTablet
    ? {}
    : {
        PopoverClasses: popoverClasses,
        marginThreshold: 0,
        BackdropProps: dynamicBackdropProps,
        ...MENU_POS_BELOW,
      };

  return (
    <Menu
      MenuListProps={{
        disablePadding: true,
        ...(props.MenuListProps ?? {}),
      }}
      {...viewportSpecificProps}
      {...props}
    />
  );
};

/**
 * Unrelated to SimpleMenu above
 */
export const useSimpleMenuState = () => {
  const [anchorEl, setAnchorEl] = useState<Element | null>(null);
  const handleClick = (e: MouseEvent) => setAnchorEl(e.currentTarget);
  const handleClose = useCallback(() => setAnchorEl(null), []);

  const isOpen = anchorEl !== null;

  return [isOpen, handleClick, handleClose, anchorEl, setAnchorEl] as const;
};

// Menu -> Popover -> Modal uses an internal SimpleBackdrop which isn't the same as Backdrop and does not actually accept BackdropProps.
// It passes all unknown properites to the div though, so we can use className to achieve the end goal
export const FULLWIDTH_MENU_BACKDROP_PROPS = {
  invisible: false,
  className: "scha__fake-partial-backdrop",
} as Partial<BackdropProps>;
